// components/SelfPaced/LessonItem.js
// Matches REPLIT lesson row design — theme-aware type icons via CSS vars
import React from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import {
  Edit, Trash2, MoreVertical, CheckCircle2,
  Upload, PlayCircle, Play, FileText, Presentation,
  File, PenLine, HelpCircle, Eye, Sparkles, RefreshCw,
  AlertCircle, Loader2,
} from 'lucide-react';
import { StatusBadge } from '../ui';
import './_self-paced-pages.scss';

const LESSON_TYPE_CONFIG = {
  video:  { icon: Play,         label: 'Video',      color: 'var(--chart-1, #3b82f6)', bg: 'hsl(var(--primary) / .08)' },
  pdf:    { icon: FileText,     label: 'PDF',        color: 'var(--chart-3, #ef4444)', bg: 'hsl(var(--destructive) / .08)' },
  ppt:    { icon: Presentation, label: 'PowerPoint', color: 'var(--chart-4, #f59e0b)', bg: 'hsl(var(--chart-4, 43 96% 56%) / .10)' },
  docx:   { icon: File,         label: 'Document',   color: 'var(--chart-5, #06b6d4)', bg: 'hsl(var(--chart-5, 187 92% 42%) / .08)' },
  text:   { icon: PenLine,      label: 'Article',    color: 'hsl(var(--muted-foreground))', bg: 'hsl(var(--muted) / .5)' },
  quiz:   { icon: HelpCircle,   label: 'Quiz',       color: 'var(--chart-2, #22c55e)', bg: 'hsl(var(--chart-2, 142 71% 45%) / .08)' },
};

const formatDuration = (min) => {
  if (!min) return '';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h > 0) return `${h}h${m > 0 ? ` ${m}m` : ''}`;
  return `${m}m`;
};

const statusMap = {
  ready: 'active',
  processing: 'pending',
  draft: 'draft',
};

// Lesson types eligible for AI explanation
const AI_ELIGIBLE_TYPES = new Set(['pdf', 'ppt', 'docx', 'text']);

const AI_STATUS_CONFIG = {
  none:       { label: '',             color: '',        icon: null },
  pending:    { label: 'AI Queued',    color: 'warning', icon: Loader2 },
  processing: { label: 'AI Running',  color: 'info',    icon: Loader2 },
  completed:  { label: 'AI Ready',    color: 'success', icon: Sparkles },
  failed:     { label: 'AI Failed',   color: 'error',   icon: AlertCircle },
};

const LessonItem = ({ lesson, onEdit, onDelete, onConfirmUpload, onPreview, onTriggerAI, aiTriggeringId }) => {
  const cfg = LESSON_TYPE_CONFIG[lesson.type] || LESSON_TYPE_CONFIG.text;
  const Icon = cfg.icon;

  const aiEligible = AI_ELIGIBLE_TYPES.has(lesson.type) && lesson.status === 'ready';
  const aiStatus = lesson.aiProcessingStatus || 'none';
  const aiCfg = AI_STATUS_CONFIG[aiStatus] || AI_STATUS_CONFIG.none;
  const isAITriggering = aiTriggeringId === lesson._id;

  return (
    <div className="sp-lesson-item">
      <div className="sp-lesson-item__left">
        <span
          className="sp-lesson-item__icon"
          style={{ color: cfg.color, background: cfg.bg }}
        >
          <Icon size={14} />
        </span>
        <div>
          <p className="sp-lesson-item__title">{lesson.title}</p>
          {lesson.duration > 0 && (
            <p className="sp-lesson-item__duration">{formatDuration(lesson.duration)}</p>
          )}
        </div>
      </div>

      <div className="sp-lesson-item__right">
        {lesson.isPreview && (
          <span className="sp-lesson-preview-badge">
            Free Preview
          </span>
        )}

        {/* AI Status Badge */}
        {aiEligible && aiStatus !== 'none' && aiCfg.icon && (
          <span className={`sp-ai-badge sp-ai-badge--${aiCfg.color}`}>
            {aiStatus === 'processing' || aiStatus === 'pending'
              ? <Loader2 size={11} className="sp-ai-badge__spin" />
              : React.createElement(aiCfg.icon, { size: 11 })
            }
            {aiCfg.label}
          </span>
        )}

        <StatusBadge status={statusMap[lesson.status] || 'draft'}>
          {lesson.status === 'ready' && <CheckCircle2 size={11} />}
          {lesson.status === 'processing' && <Upload size={11} />}
          {lesson.status || 'draft'}
        </StatusBadge>

        <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
          <Dropdown.Toggle as="button" className="app-btn app-btn--ghost app-btn--icon">
            <MoreVertical size={14} />
          </Dropdown.Toggle>
          <Dropdown.Menu
            renderOnMount
            popperConfig={{
              strategy: 'fixed',
              modifiers: [{ name: 'preventOverflow', options: { boundary: 'viewport' } }],
            }}
          >
            <Dropdown.Item onClick={() => onPreview?.(lesson)}>
              <Eye size={13} /> Preview
            </Dropdown.Item>
            <Dropdown.Item onClick={() => onEdit(lesson)}>
              <Edit size={13} /> Edit
            </Dropdown.Item>
            {lesson.status === 'processing' && (
              <Dropdown.Item onClick={() => onConfirmUpload(lesson._id)}>
                <PlayCircle size={13} /> Confirm Upload
              </Dropdown.Item>
            )}
            {/* AI Explanation actions */}
            {aiEligible && (aiStatus === 'none' || aiStatus === 'failed') && (
              <Dropdown.Item
                onClick={() => onTriggerAI?.(lesson._id)}
                disabled={isAITriggering}
              >
                {isAITriggering
                  ? <><Spinner size="sm" animation="border" style={{ width: 13, height: 13 }} /> Generating…</>
                  : <><Sparkles size={13} /> {aiStatus === 'failed' ? 'Retry AI Explanation' : 'Generate AI Explanation'}</>
                }
              </Dropdown.Item>
            )}
            {aiEligible && aiStatus === 'completed' && (
              <Dropdown.Item onClick={() => onTriggerAI?.(lesson._id, true)} disabled={isAITriggering}>
                <RefreshCw size={13} /> Regenerate AI
              </Dropdown.Item>
            )}
            <Dropdown.Divider />
            <Dropdown.Item className="text-danger" onClick={() => onDelete(lesson._id)}>
              <Trash2 size={13} /> Delete
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  );
};

export { LESSON_TYPE_CONFIG };
export default LessonItem;
